/* @flow */

import * as React from 'react';
import styled from '@emotion/styled';
import { connect } from 'react-redux';
import { withRouter, Link } from 'react-router-dom';
import {
  difference,
  keys,
  filter,
  find,
  flow,
  get,
  size,
  some,
  values,
  keyBy,
  sortBy,
  flatMap,
  map,
  intersection,
  noop
} from 'lodash/fp';
import { compose } from 'recompose';

import { setPlayers } from 'app/actions/fantasyInsiderActions';
import bonusesSelector from 'app/selectors/bonusesSelector';
import entitiesSelector from 'app/selectors/entitiesSelector';
import gamesSelector from 'app/selectors/gamesSelector';
import positionsSelector from 'app/selectors/positionsSelector';
import salaryExpenditureSelector from 'app/selectors/salaryExpenditureSelector';
import Grid from 'app/components/layout/Grid';
import GridCol from 'app/components/layout/GridCol';
import LineUp from 'app/components/challenge/LineUp';
import PlayerSelection from 'app/components/challenge/PlayerSelection';
import Responsive from 'app/components/responsive/Responsive';
import normaliseInsiderPlayer from 'app/scenes/fantasyInsider/normalisePlayer';
import {
  addBonus,
  addEntity,
  autoFillLineUp,
  smartFillLineUp,
  lockEntity,
  pollChallenge,
  removeEntity,
  resetLineUp,
  selectBonus,
  setGameFilter,
  setPositionFilter,
  stopPollingChallenge,
  unlockEntity
} from 'app/actions/challengeActions';
import t from 'app/utils/translate';
import { randomLineUpGenerator } from 'app/utils/animationUtils';
import { getCostSplit } from 'app/utils/priceUtils';
import canCompleteLineUpSelector from 'app/selectors/canCompleteLineUpSelector';
import { IN_PROGRESS } from '../../constants/challengeStatusTypes';
import lineUpSelector from '../../selectors/lineUpSelector';
import playerBonusesSelector from '../../selectors/playerBonusesSelector';
import withPxc from '../../hoc/withPxc';
import SimpleSticky from '../commons/SimpleSticky';
import ChallengeHead from './ChallengeHead';
import ChallengeNavBarMobile from './ChallengeNavBarMobile';
import ChallengeTimeStatusInfo from './ChallengeTimeStatusInfo';
import ChallengeTitleMobile from './ChallengeTitleMobile';
import OpenChallengeModal from './OpenChallengeModal';
import OpenChallengeNotifications from './OpenChallengeNotifications';
import { getInsiderSport } from '../../scenes/fantasyInsider/api';
import { checkUserLevel, isInProgress } from '../../utils/challengeUtils';
import Button from '../commons/Button';

const DESKTOP_HEADER_HEIGHT = 80;
const MOBILE_HEADER_HEIGHT = 50;

type Props = {
  activeBonus?: string,
  autoAdvancePositions: boolean,
  // balance?: number,
  bonusBalance?: number,
  bonuses?: Array<Object>,
  canCompleteTeam?: boolean,
  competitionName?: string,
  challengeId?: string,
  cost: number,
  currencyCode: string,
  disableAll: boolean,
  disableSelectLineup?: boolean,
  disableTabs?: Array<string>,
  displayWidget?: boolean,
  entryIdsToUpdate?: Array<string>,
  enableSmartLineUp?: boolean,
  gameFilter?: string,
  games: Array<Object>,
  hasError: boolean,
  hasUserEntry: boolean,
  history: Object,
  isChallengeBuilder?: boolean,
  isH2H?: boolean,
  isLocked: boolean,
  isMultiEntry?: boolean,
  isRacing?: boolean,
  isRapidfire: boolean,
  isSignedIn: boolean,
  isSubmitting: boolean,
  isTrainingChallenge?: boolean, // eslint-disable-line react/no-unused-prop-types
  isUpdating: boolean,
  editLineup?: boolean,
  lineupId?: string,
  lineUpSize: number,
  name?: string,
  numLocked: number,
  numSelected: number,
  onAdd: () => void,
  onSetInsiderPlayers: () => void,
  onAutoFill: () => void,
  onBack?: () => void,
  onBonusAdd?: (key: string, playerId: string) => void,
  onBonusSelect?: () => void,
  onClear: () => void,
  onSmartFill: () => void,
  onGameSelect: string => void,
  onLock: () => void,
  onPositionSelect: string => void,
  onRandomiseLineUp: () => void,
  onRemove: () => void,
  onSubmit: () => void,
  onUnlock: () => void,
  onUpdate?: () => void,
  onViewUpdate?: () => void, // eslint-disable-line react/no-unused-prop-types
  playerSelectionTableInfo?: Object,
  players: Array<Object>,
  playerBonuses: {
    [key: string]: {
      name: string,
      id: string
    }
  },
  pollTimestamp: number,
  positionFilter?: string,
  positions: Array<Object>,
  prizes?: Array<*>,
  pxc?: Object,
  racingType?: string,
  requiredSelections: number,
  salaryCap?: number,
  salaryExpenditure: number,
  searchTerm?: string,
  selections: Array<string>,
  showPlayerSelections?: boolean,
  showAllPositionsFilter?: boolean,
  sportName?: string,
  startChallengePolling: () => void,
  startTime?: number,
  statusId: number,
  stopChallengePolling: () => void,
  userLevel?: number,
  userLevels?: Object
};

type State = {
  addedToCart?: boolean,
  hasSubmitted: boolean,
  hasFantasyPlayers?: boolean,
  sportRound?: number,
  randomLineUp?: Array<Object>,
  widgetPlayerId?: string,
  willSubmit: boolean,
  willUpdate: boolean
};

const MultiChallengeHead = styled.div(({ theme }) => ({
  backgroundColor: theme.color.basis2,
  color: theme.color.white,
  padding: theme.spacing.default,
  textTransform: 'uppercase',
  fontSize: theme.fontSize.large,
  fontWeight: theme.fontWeight.bold,
  textAlign: 'center',
  marginBottom: theme.spacing.small,
  [theme.mediaQuery.desktopLargeOnly]: {
    textAlign: 'left'
  }
}));

class LineUpBuilder extends React.Component<Props, State> {
  props: Props;

  state: State = {
    addedToCart: false,
    hasSubmitted: false,
    hasFantasyPlayers: false,
    randomLineUp: undefined,
    widgetPlayerId: undefined,
    willSubmit: false,
    willUpdate: false,
    currentNavBarItem: 'players'
  };

  static defaultProps = {
    activeBonus: undefined,
    // balance: undefined,
    bonusBalance: undefined,
    competitionName: undefined,
    enableSmartLineUp: false,
    bonuses: [],
    canCompleteTeam: false,
    challengeId: undefined,
    disableSelectLineup: false,
    disableTabs: [],
    displayWidget: false,
    entryIdsToUpdate: [],
    gameFilter: undefined,
    isChallengeBuilder: false,
    isH2H: false,
    isTrainingChallenge: false,
    isMultiEntry: false,
    isRacing: false,
    lineupId: undefined,
    showAllPositionsFilter: true,
    editLineup: false,
    name: undefined,
    onBack: undefined,
    onBonusAdd: undefined,
    onBonusSelect: undefined,
    onViewUpdate: undefined,
    onUpdate: undefined,
    playerSelectionTableInfo: undefined,
    prizes: [],
    positionFilter: undefined,
    pxc: undefined,
    racingType: undefined,
    searchTerm: undefined,
    salaryCap: undefined,
    showPlayerSelections: false,
    sportName: undefined,
    startTime: undefined,
    userLevel: undefined,
    userLevels: undefined
  };

  async componentDidMount() {
    const {
      competitionName,
      enableSmartLineUp,
      onSetInsiderPlayers,
      startChallengePolling
    } = this.props;
    startChallengePolling();

    if (enableSmartLineUp) {
      const insiderPlayersData = await getInsiderSport(competitionName);

      if (Array.isArray(insiderPlayersData) && insiderPlayersData.length > 0) {
        this.setState({ hasFantasyPlayers: true });

        const fiPlayers = insiderPlayersData.map(p => normaliseInsiderPlayer(p));
        onSetInsiderPlayers(keyBy('insiderIdStr', fiPlayers));
      }
    }
  }

  UNSAFE_componentWillReceiveProps(nextProps: Props) {
    const { isSubmitting, isSignedIn } = this.props;
    const { willSubmit } = this.state;
    const signedOut = isSignedIn && !nextProps.isSignedIn;

    if (isSubmitting && !nextProps.isSubmitting && !nextProps.hasError) {
      this.setState({
        hasSubmitted: true,
        willSubmit: false,
        willUpdate: false
      });
    } else if (nextProps.isSubmitting || nextProps.isUpdating || signedOut) {
      this.setState({
        willSubmit: false,
        willUpdate: false
      });
    } else if (willSubmit && nextProps.hasUserEntry) {
      this.setState({
        willSubmit: false,
        willUpdate: true
      });
    }
  }

  componentDidUpdate(prevProps, prevState) {
    const { selections } = this.props;
    const { widgetPlayerId } = this.state;

    // did remove a player?
    if (selections.length < prevProps.selections.length) {
      const diff = difference(prevProps.selections, selections);
      if (diff.length === 1) {
        this.scrollTo(diff[0]);
      }
    }
    // toggled a widget?
    if (widgetPlayerId && widgetPlayerId !== prevState.widgetPlayerId) {
      this.scrollTo(widgetPlayerId);
    }
  }

  componentWillUnmount() {
    const { stopChallengePolling } = this.props;

    stopChallengePolling();
  }

  entityRefs = {};

  handleGameSelect = gameId => {
    const { onGameSelect } = this.props;

    onGameSelect(gameId);
    this.setState({
      currentNavBarItem: 'players'
    });
  };

  handlePositionSelect = pos => {
    const { onPositionSelect } = this.props;

    onPositionSelect(pos);
    this.setState({
      currentNavBarItem: 'players'
    });
  };

  updateNavBar = (newNavBarItem: ChallengeNavItem) => {
    this.setState({
      currentNavBarItem: newNavBarItem
    });
  };

  updateNavBarLineUp = () => {
    this.updateNavBar('lineup');
  };

  handleAutoFill = async () => {
    const { competitionName, enableSmartLineUp, onAutoFill, onSmartFill } = this.props;
    const { hasFantasyPlayers } = this.state;

    this.animateRandomLineUp(Date.now() + 1000);
    onAutoFill();

    if (enableSmartLineUp && hasFantasyPlayers) {
      onSmartFill({ sport: competitionName });
    }
  };

  handleCancel = () => {
    const { challengeId, cost, currencyCode, isH2H, isRapidfire, name, sportName } = this.props;
    const { addedToCart } = this.state;

    if (addedToCart && window && window.dataLayer) {
      let brand = cost === 0 ? 'Free' : 'Paid';

      if (isRapidfire) {
        brand = 'Rapidfire';
      } else if (isH2H) {
        brand = 'Head to head';
      }

      window.dataLayer.push({
        event: 'removeFromCart',
        ecommerce: {
          currencyCode,
          remove: {
            products: [
              {
                name,
                brand,
                id: challengeId,
                price: cost,
                category: sportName,
                quantity: 1
              }
            ]
          }
        }
      });
    }

    this.setState({
      addedToCart: false,
      hasSubmitted: false,
      willSubmit: false,
      willUpdate: false
    });
  };

  handleSubmit = () => {
    const {
      challengeId,
      cost,
      currencyCode,
      history,
      isH2H,
      isRapidfire,
      isSignedIn,
      name,
      sportName,
      pxc
    } = this.props;

    if (window && window.dataLayer) {
      let brand = cost === 0 ? 'Free' : 'Paid';

      if (isRapidfire) {
        brand = 'Rapidfire';
      } else if (isH2H) {
        brand = 'Head to head';
      }

      const products = [
        {
          name,
          brand,
          id: challengeId,
          price: cost,
          category: sportName,
          quantity: 1
        }
      ];

      window.dataLayer.push({
        event: 'addToCart',
        ecommerce: {
          currencyCode,
          add: {
            products
          }
        }
      });

      window.dataLayer.push({
        event: 'checkout',
        ecommerce: {
          currencyCode,
          checkout: {
            actionField: {
              step: 1
            },
            products
          }
        }
      });
    }

    this.setState(
      {
        addedToCart: true,
        willSubmit: true
      },
      () => {
        if (!isSignedIn) {
          if (!pxc.isEnabled) {
            if (window.portal) window.portal.client.showLogin();
            // history.push({ search: '?modal=signin' });
          } else {
            pxc.login();
          }
        }
      }
    );
  };

  handleConfirm = () => {
    const {
      // balance,
      bonusBalance,
      pxc,
      onSubmit,
      cost,
      name,
      challengeId,
      currencyCode,
      isRapidfire,
      isH2H,
      sportName
    } = this.props;
    const balance =
      (window && window.portal && window.portal.user && window.portal.user.account_balance()) || 0;

    const { bonus } = getCostSplit(cost, balance, bonusBalance);

    if (window && window.dataLayer) {
      let brand = cost === 0 ? 'Free' : 'Paid';

      if (isRapidfire) {
        brand = 'Rapidfire';
      } else if (isH2H) {
        brand = 'Head to head';
      }
      window.dataLayer.push({
        event: 'checkout',
        ecommerce: {
          currencyCode,
          checkout: {
            actionField: {
              step: 2
            },
            products: [
              {
                name,
                brand,
                id: challengeId,
                price: cost,
                category: sportName,
                quantity: 1
              }
            ]
          }
        }
      });

      this.setState({ addedToCart: false });
    }

    if (!pxc.isEnabled || cost - bonus <= 0) {
      onSubmit();
      return;
    }

    const transferData = {
      transactions: [
        {
          amount: bonus > 0 ? cost - bonus : cost,
          details: `${name} (Entry Fee)`,
          metadata: {
            challenge_id: challengeId
          }
        }
      ]
    };

    pxc.previewTransfer(transferData);
  };

  handleUpdate = () => {
    const { isSignedIn } = this.props;

    if (isSignedIn) {
      this.setState({
        willUpdate: true
      });
    }
  };

  setFilterAndOpenWidget = (id: number, positionOrGameId: string) => {
    const { isRacing } = this.props;

    if (isRacing) {
      this.handleGameSelect(positionOrGameId);
    } else {
      this.handlePositionSelect(positionOrGameId);
    }
    this.openWidget(id);
  };

  closeWidget = () => {
    this.setState({
      widgetPlayerId: undefined
    });
  };

  openWidget = (id: number) => {
    this.setState({
      widgetPlayerId: id
    });
  };

  toggleWidget = (id: number) => {
    this.setState(state => ({
      widgetPlayerId: state.widgetPlayerId === id ? undefined : id
    }));
  };

  animateRandomLineUp(stopTime) {
    if (Date.now() >= stopTime) {
      this.setState({
        randomLineUp: undefined
      });
      return;
    }

    const { onRandomiseLineUp } = this.props;

    this.setState({
      randomLineUp: onRandomiseLineUp()
    });
    window.requestAnimationFrame(() => {
      this.animateRandomLineUp(stopTime);
    });
  }

  scrollTo(id) {
    const foundEntity = this.entityRefs[id];
    // entity may be hidden by filters
    if (foundEntity) {
      foundEntity.scrollTop();
    }
  }

  render() {
    const {
      activeBonus,
      autoAdvancePositions,
      bonuses,
      canCompleteTeam,
      cost,
      disableAll,
      disableSelectLineup,
      disableTabs,
      displayWidget,
      entryIdsToUpdate,
      entriesMaxedOut,
      gameFilter,
      games,
      hasUserEntry,
      showAllPositionsFilter,
      isChallengeBuilder,
      isH2H,
      isLocked,
      isMultiEntry,
      isRacing,
      isSubmitting,
      isUpdating,
      editLineup,
      lineupId,
      lineUpSize,
      name,
      numLocked,
      numSelected,
      onAdd,
      onBack,
      onBonusAdd,
      onBonusSelect,
      onClear,
      onLock,
      onRemove,
      onUnlock,
      onUpdate,
      players,
      playerBonuses,
      playerSelectionTableInfo,
      pollTimestamp,
      positionFilter,
      positions,
      prizes,
      pxc,
      racingType,
      requiredSelections,
      salaryCap,
      enableSmartLineUp,
      salaryExpenditure,
      searchTerm,
      showPlayerSelections,
      startTime,
      statusId,
      userLevel,
      userLevels
    } = this.props;

    const {
      currentNavBarItem,
      hasSubmitted,
      randomLineUp,
      hasFantasyPlayers,
      willSubmit,
      willUpdate,
      widgetPlayerId
    } = this.state;

    const isOpen = statusId < IN_PROGRESS;
    const multiChallengeLabel =
      entryIdsToUpdate.length > 1
        ? `Edit Lineup (${entryIdsToUpdate.length} ${t('Challenges')})`
        : 'Edit Lineup';

    const isRestricted = !checkUserLevel(userLevels, userLevel);
    const showLineupBuilder = !entriesMaxedOut || hasUserEntry;

    return (
      <Responsive desktop>
        {isDesktop => (
          <div>
            {!isDesktop && (
              <SimpleSticky>
                <ChallengeTitleMobile
                  displayTimer={isInProgress(statusId)}
                  pollTimestamp={pollTimestamp}
                >
                  {name}
                </ChallengeTitleMobile>
                <ChallengeNavBarMobile
                  activeNavItem={currentNavBarItem}
                  isOpen={isOpen}
                  isRacing={isRacing}
                  racingType={racingType}
                  lineUpSize={lineUpSize}
                  onSelect={showLineupBuilder ? this.updateNavBar : noop}
                  requiredSelections={requiredSelections}
                  onBack={onBack}
                  showLineupBuilder={showLineupBuilder}
                />
              </SimpleSticky>
            )}
            {entryIdsToUpdate.length > 1 || editLineup ? (
              <MultiChallengeHead>
                {multiChallengeLabel}
                <ChallengeTimeStatusInfo />
              </MultiChallengeHead>
            ) : (
              <ChallengeHead
                disableTabs={disableTabs}
                isChallengeBuilder={isChallengeBuilder}
                prizes={prizes}
                showChallengeOverview={currentNavBarItem === 'overview' || !showLineupBuilder}
              />
            )}
            {!showLineupBuilder ? (
              <div>
                <p style={{ textAlign: 'center' }}>
                  You have entered the maximum amount of entries per person for this contest.
                  <br />
                  You can edit your entries in My Contests
                </p>
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center'
                  }}
                >
                  <Button style={{ marginBottom: 10 }}>
                    <Link to="/my-contests/upcoming">Go to my contests</Link>
                  </Button>
                  <Button kind="secondary">
                    <Link to="/lobby">Return to Lobby</Link>
                  </Button>
                </div>
              </div>
            ) : (
              <Grid>
                {currentNavBarItem === 'players' && (
                  <GridCol
                    width={currentNavBarItem === 'lineup' || isDesktop ? 'two-thirds' : undefined}
                  >
                    <PlayerSelection
                      autoAdvancePositions={autoAdvancePositions}
                      disabled={disableAll}
                      displayWidget={displayWidget}
                      gameFilter={gameFilter}
                      games={games}
                      hasSearch
                      isAnimating={randomLineUp !== undefined}
                      isDesktop={isDesktop}
                      isRacing={isRacing}
                      showAllPositionsFilter={showAllPositionsFilter}
                      offsetTop={isDesktop ? DESKTOP_HEADER_HEIGHT : MOBILE_HEADER_HEIGHT}
                      onAdd={onAdd}
                      onGameSelect={this.handleGameSelect}
                      onPositionSelect={this.handlePositionSelect}
                      onRemove={onRemove}
                      onReview={this.updateNavBarLineUp}
                      onWidgetClick={this.toggleWidget}
                      players={players}
                      playerSelectionTableInfo={playerSelectionTableInfo}
                      positionFilter={positionFilter}
                      positions={positions}
                      requiredSelections={requiredSelections}
                      salaryCap={salaryCap}
                      salaryExpenditure={salaryExpenditure}
                      searchTerm={searchTerm}
                      showPlayerSelections={showPlayerSelections}
                      totalSelected={numSelected}
                      widgetPlayerId={widgetPlayerId}
                      setRef={(id, ref) => {
                        this.entityRefs[id] = ref;
                      }}
                    />
                  </GridCol>
                )}
                {(currentNavBarItem === 'lineup' || isDesktop) && (
                  <GridCol width="one-third">
                    <LineUp
                      activeBonus={activeBonus}
                      bonuses={bonuses}
                      canCompleteTeam={canCompleteTeam}
                      disableSelectLineup={disableSelectLineup}
                      displayWidget={displayWidget}
                      enableSmartLineUp={enableSmartLineUp && hasFantasyPlayers}
                      editMode={entryIdsToUpdate.length > 0 || editLineup}
                      entryIdsToUpdate={entryIdsToUpdate}
                      editLineup={editLineup}
                      games={games}
                      lineupId={lineupId}
                      loadEntry={hasUserEntry}
                      isAnimating={randomLineUp !== undefined}
                      isLocked={isLocked}
                      isRacing={isRacing}
                      isRestricted={isRestricted}
                      isSubmitting={isSubmitting}
                      isUpdating={isUpdating}
                      numLocked={numLocked}
                      numSelected={numSelected}
                      offsetTop={isDesktop ? DESKTOP_HEADER_HEIGHT : MOBILE_HEADER_HEIGHT}
                      onAutoFill={this.handleAutoFill}
                      onBonusAdd={onBonusAdd}
                      onBonusSelect={onBonusSelect}
                      onClear={onClear}
                      onLock={onLock}
                      onGameSelect={this.handleGameSelect}
                      onPositionSelect={this.handlePositionSelect}
                      onRemove={onRemove}
                      onSubmit={this.handleSubmit}
                      onUnlock={onUnlock}
                      onUpdate={this.handleUpdate}
                      onWidgetClose={this.closeWidget}
                      onWidgetOpen={this.setFilterAndOpenWidget}
                      positions={randomLineUp || positions}
                      playerBonuses={playerBonuses}
                      pxc={pxc}
                      requiredSelections={requiredSelections}
                      salaryCap={salaryCap}
                      salaryExpenditure={salaryExpenditure}
                      widgetPlayerId={widgetPlayerId}
                    />
                  </GridCol>
                )}
              </Grid>
            )}
            <OpenChallengeModal
              cost={cost}
              bonusFreeCost={isH2H ? cost : 0}
              entryIdsToUpdate={entryIdsToUpdate}
              showSubmitSuccess={hasSubmitted}
              isSubmitting={isSubmitting}
              isUpdating={isUpdating}
              isChallengeBuilder={isChallengeBuilder}
              editLineup={editLineup}
              pxc={pxc}
              onCancel={this.handleCancel}
              onSubmit={this.handleConfirm}
              onUpdate={onUpdate}
              startTime={startTime}
              submitMore
              showEntryConfirmation={willSubmit}
              showUpdateConfirmation={willUpdate}
              multiEntry={isMultiEntry}
            />
            {!isChallengeBuilder && <OpenChallengeNotifications isRestricted={isRestricted} />}
          </div>
        )}
      </Responsive>
    );
  }
}

function mapStateToProps(state: GlobalState) {
  const { challenge, lineUp, playerList, settings, user } = state;
  const selections = flow(flatMap(values))(lineUp.selections);
  // const e1 = performance.now();
  const entities = entitiesSelector(state);
  // console.log('PERF: entities', performance.now() - e1);
  // const p1 = performance.now();
  const positions = positionsSelector(state);
  // console.log('PERF: positions', performance.now() - p1);

  const userSportLevels = get('user.currentUser.sport_levels', state);
  const userLevel = get(`${challenge.sportId}.level`, userSportLevels);

  const entriesMaxedOut = challenge.userEntryCount >= challenge.maxEntriesPerPerson;
  return {
    activeBonus: lineUp.activeBonus,
    autoAdvancePositions: playerList.autoAdvancePositions,
    // balance: get('user.currentUser.balance', state),
    bonusBalance: get('user.currentUser.bonusPack.totalBalance', state),
    bonuses: bonusesSelector(state),
    canCompleteTeam: canCompleteLineUpSelector(state),
    challengeId: challenge.id,
    competitionName: challenge.competitionName,
    cost: challenge.reducedCost,
    currencyCode: state.settings.currency.code,
    displayWidget: settings.displayWidget,
    enableSmartLineUp: challenge.enableSmartLineUp,
    entriesMaxedOut,
    hasError: lineUp.errorMessages.length > 0,
    gameFilter: lineUp.gameFilter,
    games: challenge.isRacing ? gamesSelector(state) : challenge.games,
    showAllPositionsFilter: lineUp.showAllPositionsFilter,
    isH2H: challenge.isH2H,
    isLocked: challenge.isLocked,
    isRacing: challenge.isRacing,
    slateName: challenge.slateName,
    slateType: challenge.slateType || 'GPP',
    racingType: challenge.racingType,
    isRapidfire: challenge.isRapidfire,
    isSignedIn: user.isSignedIn,
    isSubmitting: lineUp.isSubmitting,
    isUpdating: lineUp.isUpdating,
    numLocked: size(filter('locked', selections)),
    numSelected: size(selections),
    onRandomiseLineUp: randomLineUpGenerator(state),
    players: entities,
    positionFilter: lineUp.positionFilter,
    positions,
    playerSelectionTableInfo: challenge.playerSelectionTableInfo,
    playerBonuses: playerBonusesSelector(state),
    requiredSelections: challenge.playerQuota + challenge.teamQuota,
    salaryCap: challenge.salaryCap,
    salaryExpenditure: salaryExpenditureSelector(state),
    searchTerm: playerList.searchTerm,
    selections: flow(flatMap(keys))(lineUp.selections),
    showPlayerSelections: challenge.showPlayerSelections,
    sportName: challenge.sportName,
    startTime: challenge.startTime,
    name: challenge.name,
    statusId: challenge.statusId,
    pollTimestamp: challenge.pollTimestamp,
    lineUpSize: lineUpSelector(state).length,
    userLevel,
    userLevels: challenge.userLevels
  };
}

function mapDispatchToProps(dispatch) {
  return {
    onAutoFill() {
      dispatch(autoFillLineUp());
    },
    onSmartFill(data) {
      dispatch(smartFillLineUp(data));
    },
    onBonusAdd(key, playerId) {
      dispatch(addBonus(key, playerId));
    },
    onBonusSelect(key) {
      dispatch(selectBonus(key));
    },
    onAdd(entity, position) {
      dispatch(addEntity(entity, position));
    },
    onClear() {
      dispatch(resetLineUp());
    },
    onGameSelect(gameId) {
      dispatch(setGameFilter(gameId));
    },
    onLock(id, position) {
      dispatch(lockEntity(id, position));
    },
    onSetInsiderPlayers(players) {
      dispatch(setPlayers(players));
    },
    onPositionSelect(pos) {
      dispatch(setPositionFilter(pos));
    },
    onRemove(id, position, switchPosition) {
      dispatch(removeEntity(id, position, switchPosition));
    },
    onUnlock(id, position) {
      dispatch(unlockEntity(id, position));
    },
    stopChallengePolling() {
      dispatch(stopPollingChallenge());
    },
    startChallengePolling() {
      dispatch(pollChallenge());
    }
  };
}

// order a player's positions by the position order of the challenge
function orderByPositions(playerPosIds, positions) {
  const posIds = map('id')(positions);
  return intersection(playerPosIds)(posIds);
}

function mergeProps(stateProps, dispatchProps, ownProps) {
  const { positions, positionFilter } = stateProps;
  const { onAdd } = dispatchProps;

  return {
    ...stateProps,
    ...dispatchProps,
    ...ownProps,
    isMultiEntry: ownProps.numChallengesToCreate ? ownProps.numChallengesToCreate > 1 : false,
    disableAll: positionFilter ? get('filled', find({ id: positionFilter }, positions)) : false,
    hasUserEntry: get('entryIdsToUpdate.length')(ownProps) > 0,
    onAdd(entity, playerPositions) {
      const posKeys = map('key')(playerPositions);
      const sortedPosKeys = positionFilter
        ? sortBy(p => p !== positionFilter)(posKeys)
        : orderByPositions(posKeys, positions);
      const pos = find(
        key =>
          some(
            {
              id: key,
              filled: false
            },
            positions
          ),
        sortedPosKeys
      );
      onAdd(entity, pos);
    }
  };
}

const enhancer = compose(
  withRouter,
  withPxc,
  connect(mapStateToProps, mapDispatchToProps, mergeProps)
);

export default enhancer(LineUpBuilder);
